var _a, _AnimationBlocker_imageAddedObserver;
export class AnimationBlocker {
    constructor() {
        _AnimationBlocker_imageAddedObserver.set(this, _a.createImageAddedObserver());
        _a.hideAllAnimations();
    }
    /**
     * Creates a MutationObserver to watch for added images
     * @private
     */
    static createImageAddedObserver() {
        const targetNode = document.querySelector('body');
        // Options for the observer (which mutations to observe)
        const config = { attributes: true, childList: true, subtree: true };
        // Callback function to execute when mutations are observed
        const callback = (mutationList) => {
            for (const mutation of mutationList) {
                if (mutation.type === 'childList') {
                    if (mutation.addedNodes) {
                        for (const node of mutation.addedNodes) {
                            if (node.nodeName === 'IMG') {
                                if (this.couldBeAnimated(node)) {
                                    this.hideAnimation(node);
                                }
                            }
                        }
                    }
                    if (mutation.removedNodes.length > 0) {
                        mutation.removedNodes.forEach((removedNode) => {
                            if (removedNode.nodeName === 'IMG') {
                                if (this.couldBeAnimated(removedNode)) {
                                    this.removeBlockerUI(mutation.target);
                                }
                            }
                        });
                    }
                }
            }
        };
        // Create an observer instance linked to the callback function
        const observer = new MutationObserver(callback);
        // Start observing the target node for configured mutations
        observer.observe(targetNode, config);
        return observer;
    }
    /**
     * Waits for an image to load
     * @param image
     * @private
     */
    static async waitForImage(image) {
        return new Promise((res) => {
            if (image.complete) {
                return res();
            }
            image.onload = () => res();
            image.onerror = () => res();
        });
    }
    /**
     * Checks if the image's size has changed
     * @param image
     * @private
     */
    static imageSizeChanged(image) {
        const width = image.clientWidth;
        const height = image.clientHeight;
        const prevWidth = image.dataset.width;
        const prevHeight = image.dataset.height;
        if (!prevWidth || !prevHeight)
            return;
        return (width !== parseInt(prevWidth, 10) || height !== parseInt(prevHeight, 10));
    }
    /**
     * Creates a canvas to cover the image
     * @param image
     * @private
     */
    static createCover(image) {
        if (this.getCover(image))
            return;
        const width = image.clientWidth;
        const height = image.clientHeight;
        const parent = image.parentElement;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.setAttribute('aria-hidden', 'true');
        canvas.setAttribute('role', 'presentation');
        canvas.setAttribute('data-image-cover', 'true');
        canvas.style.position = 'absolute';
        canvas.style.top = '50%';
        canvas.style.left = '50%';
        canvas.style.transform = 'translate(-50%, -50%)';
        // Draw first frame on canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0, width, height);
        parent.style.position = 'relative';
        parent.insertBefore(canvas, image);
    }
    /**
     * Gets the canvas cover
     * @param image
     * @private
     */
    static getCover(image) {
        var _b;
        return (_b = image.parentElement) === null || _b === void 0 ? void 0 : _b.querySelector('[data-image-cover]');
    }
    /**
     * Removes the canvas cover
     * @param image
     * @private
     */
    static removeCover(image) {
        const cover = this.getCover(image);
        cover.parentElement.removeChild(cover);
    }
    static removeBlockerUI(mutationTarget) {
        var _b;
        (_b = mutationTarget.querySelector('[data-image-cover]')) === null || _b === void 0 ? void 0 : _b.remove();
    }
    /**
     * Hides the animation of an image by drawing the first frame on a canvas
     * @param image
     */
    static async hideAnimation(image) {
        // If image already has an animation controller, return
        if (image.dataset.animationController)
            return;
        // Wait until it's completely loaded
        await this.waitForImage(image);
        const parent = image.parentElement;
        let canvas = parent.querySelector('[data-image-cover]');
        const width = image.clientWidth;
        const height = image.clientHeight;
        // Store state on image
        image.setAttribute('data-animation-state', 'paused');
        if (!canvas) {
            image.setAttribute('data-width', width.toString());
            image.setAttribute('data-height', height.toString());
            this.createCover(image);
        }
        else if (canvas && this.imageSizeChanged(image)) {
            // Replace canvas
            this.removeCover(image);
            this.createCover(image);
        }
        image.dataset.animationController = JSON.stringify(this);
    }
    /**
     * Hides the animation of all images on the page
     */
    static hideAllAnimations() {
        const images = this.getAllPotentiallyAnimated();
        for (let i = 0; i < images.length; i++) {
            this.hideAnimation(images[i]);
        }
    }
    /**
     * Filters a NodeList of images to only include those that could be animated
     */
    static filterImages(images) {
        return Array.from(images).filter((image) => this.couldBeAnimated(image));
    }
    /**
     * Checks a given image's src and srcset for common animation extensions
     * @param image
     */
    static couldBeAnimated(image) {
        const src = image.src;
        const srcset = image.srcset;
        const hasExpectedExtension = this.extensions.some((extension) => src.includes(extension) || srcset.includes(extension));
        return hasExpectedExtension || image.hasAttribute('data-animated');
    }
    static getAllPotentiallyAnimated() {
        const allImages = document.querySelectorAll('img');
        return this.filterImages(allImages);
    }
}
_a = AnimationBlocker, _AnimationBlocker_imageAddedObserver = new WeakMap();
AnimationBlocker.extensions = ['.gif', '.webp'];
